import React, { useState, useRef, useEffect } from 'react';
import { Spin, Row, Col } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';
import pluralize from 'pluralize';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

import { getPartners } from 'redux/reducers/partnerSlice';
import { PartnerResponseModel } from 'api/backofficeApiClient';
import { partnerCreatedAtSelector } from 'redux/selectors/partnerSelector';
import PartnerCard from 'features/partner/PartnerCard';
import { AdminPartnerSectionEnum, AdminPartnersFilterEnum } from '../enums';
import PartnersFilter, { PartnersFilters } from './PartnersFilter';
import { typeConversionToStringArray } from 'utils/convertUtils';
import { useDidUpdate } from 'rooks';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  partnersType: AdminPartnerSectionEnum;
};

export default function AdminPartnersSection(props: Props) {
  const { partnersType } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { member, section } = queryString.parse(location.search);
  const initialFilters = {
    [AdminPartnersFilterEnum.Member]: typeConversionToStringArray(member),
  };
  const [filters, setFilters] = useState<PartnersFilters>(initialFilters);
  const infiniteScrollParentRef = useRef(null);
  const [partners, setPartners] = useState<PartnerResponseModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const partnerCreatedAt = useAppSelector(partnerCreatedAtSelector);
  const dispatch = useAppDispatch();

  useDidUpdate(() => {
    const queryData = {
      section,
      [AdminPartnersFilterEnum.Member]: filters[AdminPartnersFilterEnum.Member],
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [filters]);

  useEffect(() => {
    initInfinityScroll();
  }, [partnerCreatedAt, filters]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setPartners([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onLoadMore = async (pageNumber: number) => {
    const result = await dispatch(
      getPartners({
        pageNumber,
        isRegisteredOnMP: partnersType === AdminPartnerSectionEnum.withMP,
        companyIds: filters[AdminPartnersFilterEnum.Member],
      }),
    );

    if (getPartners.fulfilled.match(result)) {
      const loadedPartners = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newPartners = [...partners, ...loadedPartners];
      const hasMoreRows = newPartners.length < totalCount;

      setHasMore(hasMoreRows);
      setPartners(newPartners);
      setTotalCount(totalCount);
    } else {
      setHasMore(false);
    }

    setIsInitialDataLoaded(true);
  };

  const getNoPartnersMessage = () => {
    switch (partnersType) {
      case AdminPartnerSectionEnum.withMP:
        return 'There are no partners that are registered in the TalentCloud.';
      case AdminPartnerSectionEnum.withoutMP:
        return 'There are no partners that are not registered in the TalentCloud.';

      default:
        return '';
    }
  };

  const getTotalPartnersMessage = () => {
    const partnerWord = pluralize('partner', totalCount);

    switch (partnersType) {
      case AdminPartnerSectionEnum.withMP:
        return `${totalCount} ${partnerWord} registered in TalentCloud`;
      case AdminPartnerSectionEnum.withoutMP:
        return `${totalCount} ${partnerWord} not registered in TalentCloud`;

      default:
        return '';
    }
  };

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24}>
              <PartnersFilter
                filters={filters}
                onChange={setFilters}
                isReginteredOnMP={
                  partnersType === AdminPartnerSectionEnum.withMP
                }
              />
            </Col>
            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!totalCount && (
                    <span className="no-data-msg">
                      {getNoPartnersMessage()}
                    </span>
                  )}
                  {!!totalCount && (
                    <p className="page-total-count">
                      {getTotalPartnersMessage()}
                    </p>
                  )}
                </>
              )}
            </Col>
          </Row>

          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {partners.map((partner, index) => (
                  <Col
                    xs={24}
                    lg={12}
                    key={`${partner.id}-${index}`}
                    className="display-flex"
                  >
                    <PartnerCard
                      partner={partner}
                      isMemberCompanyNameClickable
                    />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
